import { ref, type Ref } from 'vue'
import { useLogger } from './useLogger'
import type { ErrorDisplay } from '../types/entityConfig'

const logger = useLogger('useErrorHandler')

const sanitizeForLog = (input: string): string => {
  return input.replace(/[\r\n\t]/g, ' ').substring(0, 200)
}

/**
 * Strip stack traces from error messages.
 * Stack traces typically start with "    at " lines or contain file paths with line numbers.
 */
const stripStackTrace = (message: string): string => {
  // Remove lines that look like stack trace entries
  const lines = message.split('\n')
  const cleaned = lines.filter(
    (line) => !/^\s+at\s/.test(line) && !/^\s*\w+@/.test(line),
  )
  return cleaned.join(' ').trim()
}

/**
 * Build a user-friendly ErrorDisplay from an error and context.
 */
const buildErrorDisplay = (
  rawMessage: string,
  context: string,
  recoverable: boolean,
): ErrorDisplay => {
  const cleaned = stripStackTrace(rawMessage)
  const truncated = cleaned.length > 200 ? cleaned.substring(0, 197) + '...' : cleaned

  return {
    title: context ? `Error in ${context}` : 'An error occurred',
    message: truncated,
    action: recoverable ? 'Please try again or contact support if the issue persists.' : 'Please contact support.',
    recoverable,
  }
}

/**
 * Retry an async operation with exponential backoff.
 * Retries up to `maxRetries` times (default 3) with delays of 1s, 2s, 4s.
 */
export const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  baseDelayMs = 1000,
): Promise<T> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation()
    } catch (err) {
      lastError = err
      if (attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt)
        logger.warn(`Retry attempt ${attempt + 1}/${maxRetries}`, {
          operation: 'retryWithBackoff',
          id: `delay:${delay}ms`,
        })
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }
  throw lastError
}

interface UseErrorHandlerReturn {
  error: Ref<string>
  errorAction: Ref<string>
  showErrorModal: Ref<boolean>
  errorDisplay: Ref<ErrorDisplay>
  handleError: (err: Error | { message: string; errors?: any[] }, context?: string) => void
  handleGraphQLError: (err: any, context: string) => void
  clearError: () => void
}

export const useErrorHandler = (): UseErrorHandlerReturn => {
  const error = ref('')
  const errorAction = ref('')
  const showErrorModal = ref(false)
  const errorDisplay = ref<ErrorDisplay>({
    title: '',
    message: '',
    action: '',
    recoverable: true,
  })

  const handleError = (err: Error | { message: string; errors?: any[] }, context = '') => {
    let message: string
    if ('errors' in err && err.errors) {
      message = err.errors[0]?.message || 'An unexpected error occurred'
    } else {
      message = err.message || 'An unexpected error occurred'
    }

    logger.error(`Error in ${sanitizeForLog(context)}: ${sanitizeForLog(message)}`, {
      operation: context || 'unknown',
    })

    error.value = message
    errorAction.value = context
    showErrorModal.value = true
    errorDisplay.value = buildErrorDisplay(message, context, true)
  }

  const handleGraphQLError = (err: any, context: string) => {
    if (err.errors) {
      const errorMessages = err.errors
        .map((e: any) => sanitizeForLog(e.message || ''))
        .join('; ')
      logger.error(`GraphQL error in ${sanitizeForLog(context)}: ${errorMessages}`, {
        operation: context,
      })
      error.value = errorMessages
    } else {
      const message = sanitizeForLog(err.message || 'GraphQL operation failed')
      logger.error(`GraphQL error in ${sanitizeForLog(context)}: ${message}`, {
        operation: context,
      })
      error.value = message
    }
    errorAction.value = context
    showErrorModal.value = true
    errorDisplay.value = buildErrorDisplay(error.value, context, true)
  }

  const clearError = () => {
    error.value = ''
    errorAction.value = ''
    showErrorModal.value = false
    errorDisplay.value = { title: '', message: '', action: '', recoverable: true }
  }

  return {
    error,
    errorAction,
    showErrorModal,
    errorDisplay,
    handleError,
    handleGraphQLError,
    clearError,
  }
}
